import React, { useEffect, useState } from 'react';
import { aopApi, agentForWorkflow, agentLabel, formatDateTime, runDuration, workflowLabel, AGENT_LABELS, platformForWorkflow, platformLabel } from '../aopApi';

const pillStyle = { width: 'auto', background: 'var(--pill)', border: 'none', color: 'var(--text-sec)', cursor: 'pointer', fontFamily: 'inherit', fontSize: 11, fontWeight: 500, padding: '5px 28px 5px 10px' };

const TRIGGER_LABELS = { schedule: 'Scheduled', manual: 'Manual', api: 'API' };

export default function WorkflowRuns({ onRunClick }) {
  const [runs, setRuns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filterAgent, setFilterAgent] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [filterPlatform, setFilterPlatform] = useState('all');

  const load = () => {
    setLoading(true);
    aopApi
      .runs({ limit: 100 })
      .then((res) => {
        if (!res.success) throw new Error(res.error || 'Failed to load workflow runs');
        setRuns(res.runs || []);
        setError(null);
      })
      .catch((err) => setError(err.message))
      .finally(() => setLoading(false));
  };

  useEffect(load, []);

  const filtered = runs.filter((r) => {
    if (filterPlatform !== 'all' && platformForWorkflow(r.workflow) !== filterPlatform) return false;
    if (filterAgent !== 'all' && agentForWorkflow(r.workflow) !== filterAgent) return false;
    if (filterStatus !== 'all' && r.status !== filterStatus) return false;
    return true;
  });

  const count = (status) => runs.filter((r) => r.status === status).length;

  return (
    <div>
      <div className="aop-page-header">
        <div>
          <h1 className="aop-page-title">Workflow Runs</h1>
          <p className="aop-page-subtitle">All agent workflow executions — click a run for step-by-step trace</p>
        </div>
        <div className="aop-page-actions">
          <button className="aop-btn ghost" onClick={load}>Refresh</button>
        </div>
      </div>

      {/* Stats */}
      <div className="aop-stats-row" style={{ marginBottom: 20 }}>
        {[
          { label: 'Total Runs', value: runs.length, color: 'var(--text)' },
          { label: 'Running', value: count('running'), color: 'var(--blue)' },
          { label: 'Completed', value: count('completed'), color: 'var(--green)' },
          { label: 'Failed', value: count('failed'), color: 'var(--red)' },
        ].map((s) => (
          <div key={s.label} className="aop-stat-card">
            <div className="aop-stat-value" style={{ color: s.color }}>{s.value}</div>
            <div className="aop-stat-label">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filters */}
      <div className="aop-filter-bar">
        <select className="aop-pill" value={filterPlatform} onChange={(e) => setFilterPlatform(e.target.value)} style={pillStyle}>
          <option value="all">All Platforms</option>
          <option value="aop">AOP</option>
          <option value="cop">COP</option>
        </select>
        <select className="aop-pill" value={filterAgent} onChange={(e) => setFilterAgent(e.target.value)} style={pillStyle}>
          <option value="all">All Agents</option>
          {Object.entries(AGENT_LABELS).map(([id, label]) => (
            <option key={id} value={id}>{label}</option>
          ))}
        </select>
        <select className="aop-pill" value={filterStatus} onChange={(e) => setFilterStatus(e.target.value)} style={pillStyle}>
          <option value="all">All Status</option>
          <option value="running">Running</option>
          <option value="completed">Completed</option>
          <option value="failed">Failed</option>
        </select>
        <div className="aop-filter-bar-spacer" />
        <span style={{ fontSize: 11, color: 'var(--text-dim)' }}>{filtered.length} runs</span>
      </div>

      {error && (
        <div style={{ background: 'rgba(255,98,93,0.1)', border: '1px solid rgba(255,98,93,0.25)', borderRadius: 6, padding: '10px 14px', marginBottom: 16, fontSize: 12, color: 'var(--red)' }}>
          {error}
        </div>
      )}

      {/* Table */}
      <div className="aop-table-wrap">
        <table className="aop-table">
          <thead>
            <tr>
              <th>Workflow</th>
              <th>Platform</th>
              <th>Agent</th>
              <th>Trigger</th>
              <th>Current Step</th>
              <th>Status</th>
              <th>Duration</th>
              <th>Started</th>
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={8}><div className="aop-empty">Loading…</div></td></tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={8}>
                  <div className="aop-empty">
                    <div className="aop-empty-icon">◎</div>
                    {runs.length === 0 ? 'No workflow runs yet — use Manual Trigger or start the AOP worker' : 'No runs match the current filters'}
                  </div>
                </td>
              </tr>
            ) : filtered.map((r) => {
              const agent = agentForWorkflow(r.workflow);
              return (
                <tr key={r._id} onClick={() => onRunClick(r._id)} title="Click to view trace">
                  <td><span className="cell-primary">{workflowLabel(r.workflow)}</span></td>
                  <td><span className={`aop-badge ${platformForWorkflow(r.workflow)}`}>{platformLabel(platformForWorkflow(r.workflow))}</span></td>
                  <td><span className={`aop-agent-tag ${agent}`}>{agentLabel(agent)}</span></td>
                  <td><span className="text-muted">{TRIGGER_LABELS[r.triggerType] || r.triggerType}</span></td>
                  <td><span className="text-muted">{r.status === 'completed' ? 'Completed' : r.currentStep || '—'}</span></td>
                  <td><span className={`aop-badge ${r.status}`}>{r.status}</span></td>
                  <td><span className="text-muted">{runDuration(r)}</span></td>
                  <td><span className="text-muted">{formatDateTime(r.startedAt || r.createdAt)}</span></td>
                </tr>
              );
            })}
          </tbody>
        </table>
        <div className="aop-pagination">
          <span>Showing {filtered.length} of {runs.length} runs</span>
        </div>
      </div>
    </div>
  );
}
